import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, FormInput, FormNumberInput, FormSelect, Modal } from '@/components/UI';
import { useTradeLogicDefinitions, useTradeLogicDefinitionVersions } from '@/features/tradeLogicAuthoring/api';
import {
    buildPromotionIdempotencyKey,
    runLenses,
    type IdeaLabLens,
    type IdeaLabPromotionReceipt,
    type IdeaLabRunDetail,
    type IdeaLabRunVariant,
    type PromoteIdeaLabVariantInput
} from '../ideaLabUtils';
import { marketGroupLabel, promotableLenses } from '../reportView';
import PromotionReceiptCard from './PromotionReceiptCard';

type EntityTypeChoice = 'Asset' | 'AssetIndex' | 'Cryptocurrency';
type SizeType = PromoteIdeaLabVariantInput['entryTransactionSizeType'];

interface PromoteVariantModalProps {
    run: IdeaLabRunDetail;
    variant: IdeaLabRunVariant | null;
    isOpen: boolean;
    /** Lens to preselect, e.g. the one the report shows; ignored when it cannot be promoted. */
    initialLens?: IdeaLabLens | null;
    initialMarket?: EntityTypeChoice | null;
    onClose: () => void;
}

/**
 * Promote to Strategy: republishes the frozen variant the run evaluated and creates the inactive
 * strategy that trades it, in one server-side transaction. The entry is carried over exactly; the
 * exit and sizing are chosen here because the run did not measure them.
 */
const PromoteVariantModal = ({ run, variant, isOpen, initialLens = null, initialMarket = null, onClose }: PromoteVariantModalProps) => {
    const queryClient = useQueryClient();
    const runEntityTypes = useMemo(
        () =>
            [...new Set(run.entities.map((entity) => entity.entityType))].filter(
                (type): type is EntityTypeChoice => type === 'Asset' || type === 'AssetIndex' || type === 'Cryptocurrency'
            ),
        [run.entities]
    );

    const [idempotencyKey, setIdempotencyKey] = useState('');
    const [strategyName, setStrategyName] = useState('');
    const [strategyDescription, setStrategyDescription] = useState('');
    const [exitDefinitionId, setExitDefinitionId] = useState<number | null>(null);
    const [exitVersionId, setExitVersionId] = useState<number | null>(null);
    const [sizeType, setSizeType] = useState<SizeType>('percentageValue');
    const [sizeValue, setSizeValue] = useState<number | null>(5);
    const [allowedTypes, setAllowedTypes] = useState<EntityTypeChoice[]>([]);
    const [sampleSize, setSampleSize] = useState<number | null>(3);
    const measuredLenses = useMemo(() => runLenses(run), [run]);
    const lenses = useMemo(() => promotableLenses(run), [run]);
    const unpromotableLenses = measuredLenses.filter((candidate) => !lenses.includes(candidate));
    const [lens, setLens] = useState<IdeaLabLens>(lenses[0] ?? 'underlying');
    const [receipt, setReceipt] = useState<IdeaLabPromotionReceipt | null>(null);
    const optionLeg = lens === 'option' ? run.optionLens : null;
    useEffect(() => {
        if (lens === 'futures') {
            setSizeType('percentageRiskCapital');
            setSizeValue(100);
        }
    }, [lens]);

    // A fresh key per dialog: repeat clicks in this dialog return the same receipt.
    useEffect(() => {
        if (!isOpen || !variant) return;

        setIdempotencyKey(buildPromotionIdempotencyKey(run.id, variant.variantId));
        setStrategyName(`${variant.label} (Idea Lab run ${run.id})`);
        setStrategyDescription(`Promoted from Idea Lab study ${run.studyId}, variant ${variant.variantKey}, run ${run.id}.`);
        setAllowedTypes(initialMarket === null ? [] : [initialMarket]);
        setReceipt(null);
        setLens(initialLens !== null && lenses.includes(initialLens) ? initialLens : (lenses[0] ?? 'underlying'));
    }, [isOpen, variant, run.id, run.studyId, runEntityTypes, lenses, initialLens, initialMarket]);

    const groups = runEntityTypes.flatMap((entityType) =>
        lenses
            .filter(
                (candidate) =>
                    !(candidate === 'underlying' && entityType === 'AssetIndex') &&
                    !(candidate === 'option' && entityType === 'Cryptocurrency')
            )
            .map((candidate) => ({ entityType, lens: candidate, label: marketGroupLabel(entityType, candidate) }))
    );

    const exitDefinitions = useTradeLogicDefinitions({ kind: 'exit', isActiveOnly: true, enabled: isOpen });
    const exitVersions = useTradeLogicDefinitionVersions({ definitionId: exitDefinitionId, enabled: isOpen });

    useEffect(() => {
        if (exitVersions.versions.length > 0 && exitVersionId === null) {
            setExitVersionId(exitVersions.versions[0]!.versionId);
        }
    }, [exitVersions.versions, exitVersionId]);

    const promoteMutation = useMutation({
        ...trpc.ideaLab.promoteVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (created) => {
            showToast(`Created inactive strategy #${created.strategyId}`, 'success');
            setReceipt(created);
            await Promise.all([
                queryClient.invalidateQueries(trpc.ideaLab.listPromotions.pathFilter()),
                queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter()),
                queryClient.invalidateQueries(trpc.ideaLab.getVariant.pathFilter())
            ]);
        }
    });

    const validation = useMemo(() => {
        if (!variant) return 'Select a variant.';
        if (lenses.length === 0) return 'This run has no lens that can be promoted.';
        if (strategyName.trim() === '') return 'Name the strategy.';
        if (exitVersionId === null) return 'Choose the exit logic the strategy will use.';
        if (sizeValue === null || !(sizeValue > 0) || sizeValue > 100) return 'Entry size must be between 0 and 100 percent.';
        if (allowedTypes.length !== 1) return 'Choose one measured market group.';
        if (optionLeg !== null && allowedTypes.includes('Cryptocurrency')) return 'An option leg supports stocks and indexes.';
        if (optionLeg === null && allowedTypes.includes('AssetIndex'))
            return 'Cash indexes cannot be traded directly. Choose the option lens or remove indexes from the strategy.';

        return null;
    }, [variant, lenses.length, strategyName, exitVersionId, sizeValue, allowedTypes, optionLeg]);

    const submit = () => {
        if (!variant || validation !== null || exitVersionId === null || sizeValue === null) return;

        promoteMutation.mutate({
            variantId: variant.variantId,
            runId: run.id,
            idempotencyKey,
            strategyName: strategyName.trim(),
            strategyDescription: strategyDescription.trim() === '' ? null : strategyDescription.trim(),
            exitVersionId,
            entryTransactionSizeType: sizeType,
            entryTransactionSizeValue: sizeValue,
            allowedUnderlyingEntityTypes: allowedTypes,
            universeMode: 'runEntities',
            lens,
            ...(sampleSize !== null ? { verificationSampleSize: sampleSize } : {})
        });
    };

    return (
        <Modal
            footer={
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-amber-300">{receipt ? '' : (validation ?? '')}</span>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={onClose}>
                            {receipt ? 'Close' : 'Cancel'}
                        </Button>
                        {!receipt && (
                            <Button
                                disabled={validation !== null || promoteMutation.isPending}
                                isLoading={promoteMutation.isPending}
                                variant="primary"
                                onClick={submit}
                            >
                                Promote to strategy
                            </Button>
                        )}
                    </div>
                </div>
            }
            isOpen={isOpen}
            size="lg"
            title={variant ? `Promote "${variant.label}"` : 'Promote variant'}
            onClose={onClose}
        >
            {receipt ? (
                <div className="space-y-3">
                    <Callout variant="success">
                        The strategy exists and is inactive. Verification below re-fetched a sample of the run&apos;s entities through the
                        normal strategy path and compared the entries with what the run measured.
                    </Callout>
                    <PromotionReceiptCard promotion={receipt} variantLabel={variant?.label} />
                </div>
            ) : (
                <div className="space-y-4">
                    <Callout variant="info">
                        The entry logic is republished exactly as the run measured it, with the run&apos;s duration, session settings,
                        entity list and regime pins carried over. Promotion refuses if the current compiler or feature manifest would
                        compile a different graph. The exit and sizing below were not measured by the run.
                    </Callout>

                    {unpromotableLenses.includes('option') && (
                        <Callout variant="warning">
                            The option results of this run cannot be promoted: the run recorded no call or put choice. Promote the
                            underlying here, or run again with Call or Put chosen.
                        </Callout>
                    )}
                    <FormSelect
                        label="Measured group to promote"
                        noMargin
                        options={[
                            { value: '', label: 'Choose a group' },
                            ...groups.map((group) => ({ value: `${group.entityType}:${group.lens}`, label: group.label }))
                        ]}
                        value={allowedTypes.length === 1 ? `${allowedTypes[0]}:${lens}` : ''}
                        onChange={(event) => {
                            const group = groups.find((candidate) => `${candidate.entityType}:${candidate.lens}` === event.target.value);
                            setAllowedTypes(group ? [group.entityType] : []);
                            if (group) setLens(group.lens);
                        }}
                        helpText="Creates one inactive strategy for the chosen group. Promote additional groups separately. Index movement is research only."
                    />

                    <FormInput
                        label="Strategy name"
                        maxLength={255}
                        noMargin
                        value={strategyName}
                        onChange={(event) => setStrategyName(event.target.value)}
                    />
                    <FormInput
                        label="Description"
                        maxLength={4000}
                        noMargin
                        value={strategyDescription}
                        onChange={(event) => setStrategyDescription(event.target.value)}
                    />

                    <div className="grid gap-3 sm:grid-cols-2">
                        <FormSelect
                            label="Exit logic"
                            noMargin
                            options={[
                                { value: '', label: exitDefinitions.isLoading ? 'Loading…' : 'Select exit logic' },
                                ...exitDefinitions.definitions.map((definition) => ({
                                    value: definition.definitionId,
                                    label: definition.name
                                }))
                            ]}
                            value={exitDefinitionId ?? ''}
                            onChange={(event) => {
                                setExitDefinitionId(event.target.value === '' ? null : Number(event.target.value));
                                setExitVersionId(null);
                            }}
                        />
                        <FormSelect
                            disabled={exitDefinitionId === null}
                            label="Exit version"
                            noMargin
                            options={[
                                { value: '', label: exitVersions.isLoading ? 'Loading…' : 'Select a version' },
                                ...exitVersions.versions.map((version) => ({ value: version.versionId, label: `v${version.version}` }))
                            ]}
                            value={exitVersionId ?? ''}
                            onChange={(event) => setExitVersionId(event.target.value === '' ? null : Number(event.target.value))}
                        />
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <FormSelect
                            disabled={lens === 'futures'}
                            label="Entry size"
                            noMargin
                            options={[
                                { value: 'percentageValue', label: '% of portfolio value' },
                                { value: 'percentageRiskCapital', label: '% of risk capital' }
                            ]}
                            value={sizeType}
                            onChange={(event) => setSizeType(event.target.value as SizeType)}
                        />
                        <FormNumberInput
                            disabled={lens === 'futures'}
                            label="Size"
                            max={100}
                            min={0.01}
                            noMargin
                            step={0.5}
                            suffix="%"
                            value={sizeValue}
                            onValueChange={setSizeValue}
                        />
                    </div>

                    {lens === 'futures' && (
                        <Callout variant="info">
                            The inactive strategy keeps the measured ETF subjects and native route, cost assumptions, warmup, allocation and
                            close buffer. Its leg uses 100% of that configured research allocation. The chosen exit was not measured by this
                            run; ordinary and portfolio backtests validate it separately.
                        </Callout>
                    )}
                    <Callout variant="info">
                        Only the selected group’s {run.entities.filter((entity) => allowedTypes.includes(entity.entityType)).length} frozen
                        entities and market timing will carry forward. Crypto options are not applicable. Other groups remain in the Idea
                        Lab report.
                    </Callout>

                    <FormNumberInput
                        helpText={
                            optionLeg !== null
                                ? 'Run entities re-fetched through the strategy path after creation; their entries and their finder-selected contracts are compared with what the run measured.'
                                : 'Run entities re-fetched through the strategy path after creation and compared with the measured entries.'
                        }
                        integer
                        label="Verification sample"
                        max={10}
                        min={1}
                        noMargin
                        value={sampleSize}
                        onValueChange={setSampleSize}
                    />
                </div>
            )}
        </Modal>
    );
};

export default PromoteVariantModal;
